import React from 'react';
import MarkdownBody from '@/modules/topic/read/MarkdownBody';
import WhereWhenBlock from '@/modules/topic/read/WhereWhenBlock';
import CompositeEngagementBlock from '@/modules/topic/read/CompositeEngagementBlock';
import ChangesLinkBlock from '@/modules/topic/read/ChangesLinkBlock';
import RegistrationBlock from '@/modules/topic/read/RegistrationBlock';
import ListingManagersBlock from '@/modules/topic/read/ListingManagersBlock';
import TopicOverflowMenu from '@/modules/topic/read/TopicOverflowMenu';
import TopicTypeBadge from '@/modules/topic/read/TopicTypeBadge';
import {
  heroRowStyle,
  heroImageColStyle,
  heroImageStyle,
  heroInfoColStyle,
  heroTitleRowStyle,
  heroTitleStyle,
  readBodyStyle,
  readHeroStyle,
} from '@/modules/topic/read/styles';
import type { TopicReadBodyProps } from '../types';

/**
 * Deal read-only body — a deal is a date-anchored type, so its location and
 * date/time lines render via the shared `WhereWhenBlock` and it contributes no
 * extra type section. Composed inline (rather than delegating to a shared view) so the event
 * read experience can diverge from the other types over time.
 */
const ReadOnlyView: React.FC<TopicReadBodyProps> = ({
  topic,
  topicId,
  registration,
  deleting,
  registrationEnabled,
  topicMutations,
  onEdit,
  onRequestDelete,
  onOpenPath,
  onGuardSuccess,
}) => (
  <>
    <div style={readHeroStyle}>
      <div style={heroRowStyle}>
        {topic.imageData && (
          <div style={heroImageColStyle}>
            <img
              src={topic.imageData}
              alt=""
              data-testid="topic-image-preview"
              style={heroImageStyle}
            />
          </div>
        )}
        <div style={heroInfoColStyle}>
          <div style={heroTitleRowStyle}>
            <h1 style={heroTitleStyle} data-testid="topic-readonly-title">
              {topic.title}
            </h1>
            <TopicOverflowMenu
              topicId={topicId}
              deleting={deleting}
              onEdit={onEdit}
              onRequestDelete={onRequestDelete}
            />
          </div>
          <TopicTypeBadge topic={topic} />
        </div>
      </div>
    </div>

    <div style={readBodyStyle} data-testid="topic-readonly">
      {topic.body && (
        <MarkdownBody body={topic.body} containerTestId="topic-readonly-body" />
      )}
      <WhereWhenBlock topic={topic} />
      <CompositeEngagementBlock
        topicId={topicId}
        topicTitle={topic.title}
        onMetricsOpen={onOpenPath}
        topic={topic}
      />
      <RegistrationBlock
        topicId={topicId}
        registration={registration}
        registrationEnabled={registrationEnabled}
        onGuardSuccess={onGuardSuccess}
      />
      <ChangesLinkBlock
        topicId={topicId}
        hasMutations={topicMutations.length > 0}
      />
      <ListingManagersBlock managerEmails={topic.managerEmails ?? []} />
    </div>
  </>
);

export default ReadOnlyView;
